import { AutoAwesome } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import Page from 'src/components/mui/Page'
import ColoredTooltip from 'src/components/Tooltip/ColoredTooltip'
import { useErrorData } from 'src/context/ErrorContext'
import { useFilter } from 'src/context/FilterContext'
import { DefaultService, DelIntelAgg, DelIntelThemeGroupby } from 'src/services/openApiV2'
import useCustomStartDate from 'src/utils/hooks/useCustomStartDate'
import PreviewPage from '../Preview'
import ErrorInsightsSummary from './components/Summary/ErrorInsightsSummary'
import ErrorInsightsTable from './components/Table/ErrorInsightsTable'

const ErrorInsights = () => {
  const { getFilters, getFiltersV2 } = useFilter()
  const { handleError } = useErrorData()
  const { customEndDate } = useCustomStartDate()

  const [themesData, setThemesData] = useState<DelIntelThemeGroupby[]>([])
  const [themesDataLoading, setThemesDataLoading] = useState(true)
  const [aggregateData, setAggregateData] = useState<DelIntelAgg>(null)
  const [aggregateDataLoading, setAggregateDataLoading] = useState(true)
  const [selectedTheme, setSelectedTheme] = useState<DelIntelThemeGroupby | null>(null)

  const filterArr = ['b_name', 'vb_name', 'vb_platform', 'am_name', 'chain', 'start_date', 'end_date']

  const filterObj = useMemo(() => {
    return getFiltersV2(filterArr, true)
  }, [...getFilters(filterArr)])

  const getThemesData = async () => {
    setThemesDataLoading(true)
    try {
      const res = await DefaultService.callDelIntelDelIntelThemeGroupbyApiDelIntelDelIntelThemeGroupbyPost({
        requestBody: filterObj
      })
      if (res) setThemesData(res)
    } catch (err) {
      handleError(err?.message)
    }
    setThemesDataLoading(false)
  }

  const getAggregateData = async () => {
    setAggregateDataLoading(true)
    try {
      const res = await DefaultService.callDelIntelDelIntelAggApiDelIntelDelIntelAggPost({
        requestBody: filterObj
      })
      if (res && res[0]) setAggregateData(res[0])
    } catch (err) {
      handleError(err?.message)
    }
    setAggregateDataLoading(false)
  }

  useEffect(() => {
    if (!filterObj) return

    getThemesData()
    getAggregateData()
  }, [filterObj])

  return (
    <Page
      title={
        <Box
          display="flex"
          alignItems="center"
          gap={1}>
          <Typography
            fontSize={20}
            fontWeight={600}>
            Base || Delivery Intelligence
          </Typography>
          <ColoredTooltip
            title="AI-powered feature. Labeling and calculations may have errors. Review carefully."
            placement="right">
            <Box
              px={1}
              height="22px"
              borderRadius="10px"
              border="1px solid #E6E6E6"
              display="flex"
              alignItems="center"
              gap={0.5}>
              <AutoAwesome sx={{ fontSize: '15px' }} />
              <Typography
                fontSize={12}
                color="#000"
                lineHeight="initial"
                sx={{ cursor: 'default' }}>
                AI-Generated (BETA)
              </Typography>
            </Box>
          </ColoredTooltip>
        </Box>
      }
      filterProps={{
        hideFilterKeys: []
      }}>
      <Box
        px={4}
        py={3}
        display="flex"
        flexDirection="column"
        gap={2}>
        {customEndDate === null ? (
          <PreviewPage
            modulePageName="Base || Delivery Intelligence"
            padding={false}
          />
        ) : (
          <>
            <ErrorInsightsSummary
              aggregateData={aggregateData}
              aggregateDataLoading={aggregateDataLoading}
              themesData={themesData}
              themesDataLoading={themesDataLoading}
              onClick={(theme) => setSelectedTheme(themesData.find((t) => t.theme === theme))}
            />
            <ErrorInsightsTable
              themesData={themesData}
              themesDataLoading={themesDataLoading}
              selectedTheme={selectedTheme}
              onClick={(theme) => setSelectedTheme(themesData.find((t) => t.theme === theme))}
            />
          </>
        )}
      </Box>
    </Page>
  )
}

export default ErrorInsights
